"use client"

import React from 'react'
import {Box, CircularProgress} from "@mui/material"
import {useLocale, useTranslations} from "use-intl"
import {useAppStore} from "@/store/AppStore"
import {pathStatusRequest} from "@/utils/api/PathStatus"
import {pathRequest} from "@/utils/api/Path"
import {segmentRequest} from "@/utils/api/Segment"
import {Scan} from "@/types/Scan"
import {Segment} from "@/types/Segments"
import {useRouter} from "@/i18n/routing"
import {walkGeoRequest} from "@/utils/api/Walk"

const TagScanSegment = ({handleNext, handleBack}: { handleNext: () => void, handleBack: () => void }) => {
  const locale = useLocale()
  const t = useTranslations("Walk")
  const no_points = t("no_points")
  const router = useRouter()

  // Centralizzazione dello stato dell'app
  const {
    user,
    setRefresh,
    setQrCode,
    setScanningDirection,
    setScanningTransport,
    setScanningPath,
    setScanningSegments,
    cleanResultsAndErrors,
    setError
  } = useAppStore((state) => state)

  const [locationState, setLocationState] = React.useState<{
    positionPermission: boolean
    userPosition?: { lat: number, lng: number }
  }>({
    positionPermission: false,
  })

  const [fetchedScans, setFetchedScans] = React.useState<Scan[] | undefined>()

  // Helper per ottenere la posizione dell'utente
  const getUserPosition = React.useCallback(() => {
    navigator.permissions
      .query({name: "geolocation"})
      .then(({state}) => {
        if (state === "granted" || state === "prompt") {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              setLocationState({
                positionPermission: true,
                userPosition: {lat: position.coords.latitude, lng: position.coords.longitude},
              })
            },
            (error) => console.error("Errore nella richiesta della posizione: ", error)
          )
        }
      })
      .catch((error) =>
        console.error("Errore durante il controllo dei permessi di geolocalizzazione:", error)
      )
  }, [])

  // Effetto per ottenere la posizione quando il componente viene montato
  React.useEffect(() => {
    getUserPosition()
  }, [getUserPosition])

  // Funzione per ottenere lo stato del percorso
  const fetchPathStatus = React.useCallback(
    async (pathId: string) => {
      if (!user?.access_token) return false
      const fetchedStatus = await pathStatusRequest(user.access_token, pathId)
      if (!fetchedStatus) return setRefresh(true)

      const instance = fetchedStatus.instances?.[0]
      setScanningDirection(instance?.direction ?? undefined)
      setScanningTransport(instance?.transport ?? undefined)
      return true
    },
    // React Compiler richiede la dep su `user` (non `user?.access_token`)
    // per allinearsi al modo in cui inferisce le dipendenze; la
    // funzione continua a leggere solo `user?.access_token` internamente.
    [user, setRefresh, setScanningDirection, setScanningTransport]
  )

  // Funzione per ottenere i dati del percorso
  const fetchPathInfo = React.useCallback(
    async (pathId: string) => {
      const pathData = await pathRequest(pathId)
      if (pathData) setScanningPath(pathData)
    },
    [setScanningPath]
  )

  // Effetto per elaborare le scansioni
  React.useEffect(() => {
    const handleGeoProcessing = async (scans: Scan[]) => {
      setQrCode(scans)
      cleanResultsAndErrors()
      if (scans?.length && user?.access_token) {
        try {
          const pathId = scans[0]?.path_id
          if (pathId && (await fetchPathStatus(pathId))) {
            await fetchPathInfo(pathId)
          } else {
            router.refresh()
          }

          const segments = await Promise.all(
            scans.map((scan) => segmentRequest(scan.segment_id, locale))
          )
          const validSegments = segments.filter((segment) => segment !== false) as Segment[]
          setScanningSegments(validSegments)
          handleNext()
        } catch (error) {
          console.error("Errore durante l'elaborazione delle scansioni:", error)
        }
      }
    }
    if (fetchedScans) {
      handleGeoProcessing(fetchedScans)
    }
  }, [
    fetchedScans,
    user?.access_token,
    locale,
    router,
    setQrCode,
    cleanResultsAndErrors,
    handleNext,
    setScanningSegments,
    fetchPathStatus,
    fetchPathInfo,
  ])

  // Effetto per ottenere scansioni basate sulla posizione
  React.useEffect(() => {
    const fetchScans = async () => {
      if (!user?.access_token || !locationState.userPosition) return
      cleanResultsAndErrors()
      const scans = await walkGeoRequest(
        user.access_token,
        locale,
        String(locationState.userPosition.lat),
        String(locationState.userPosition.lng)
      )
      if (scans && scans.length > 0) {
        setFetchedScans(scans)
        return
      }
      if (scans && scans.length === 0) setError({message: no_points, error_code: "400"})
      if (!scans) setError({message: "Error!", error_code: "400"})
      handleBack()
    }

    if (locationState.userPosition) {
      window.scrollTo({
        top: 0,
        behavior: "smooth"
      })
      fetchScans()
    }
  }, [locationState.userPosition, user?.access_token, locale, setError, handleNext, handleBack, no_points, cleanResultsAndErrors])

  return (
    <Box sx={{marginTop: "2rem"}}>
      {!locationState.positionPermission ? (
        <div>{t("missing_permissions")}</div>
      ) : (
        <div style={{display: "flex", justifyContent: "center"}}>
          <CircularProgress/>
        </div>
      )}
    </Box>
  )
}

export default TagScanSegment
